import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
	extractSurgeryById,
} from "@/features/events/features/surgeries/hooks/useExtractSurgery";
import { useSurgeryMutation } from "@/features/events/features/surgeries/api/upsertSurgery";
import { SideZ } from "@/types";
import { ui } from "@/components/ui";
import { SurgeryFormFields } from "@/features/events/features/surgeries/components/SurgeryFormFields";
import { components as c } from "@/components";
import {
	useEventStepper,
} from "@/features/templates/hooks/useEventNavigationStore";
import { diaryByIdQueryOptions } from "../../../../../../../../features/diaries";

export const SurgerySchemaZ = z.object({
	event_id: z.string().uuid(),
	surgery_id: z.string().uuid(),
	side: SideZ,
	surgery_option_id: z.string().uuid(),
	orthopaedic_area: z.string().optional(),
});

export type SurgerySchemaT = z.infer<typeof SurgerySchemaZ>;

const SurgeryDetailsPage = () => {
	const navigate = useNavigate();

	const { diaryId, eventId, surgeryId } = Route.useParams();

	const { surgery } = Route.useLoaderData();

	const surgeryMutation = useSurgeryMutation({ diaryId });

	const form = useForm<SurgerySchemaT>({
		resolver: zodResolver(
			SurgerySchemaZ,
		),
		defaultValues: {
			surgery_id: surgeryId,
			event_id: eventId,
			side: surgery?.side ?? undefined,
			surgery_option_id: surgery?.surgery_option_id ?? undefined,
		},
	});

	const { steps } = useEventStepper("surgery-details");

	const handleNext = (formData: SurgerySchemaT) => {
		surgeryMutation.mutate(
			formData,
			{
				onSuccess: () =>
					navigate({
						to: "/patients/$diaryId",
						params: { diaryId },
					}),
			},
		);
	};

	const handlePrevious = () => {
		navigate({
			to: "/new/diary/$diaryId/events/$eventId/surgery/$surgeryId",
		});
	};

	return (
		<>
			<c.cc.Stepper steps={steps} />
			<ui.Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleNext)}
					className="space-y-6"
				>
					<SurgeryFormFields
						sideFieldName="side"
						orthopaedicAreaFieldName="orthopaedic_area"
						surgeryOptionIdFieldName="surgery_option_id"
					/>

					{/* Navigation Buttons */}
					<div className="flex justify-between gap-4 mt-8">
						<ui.Button
							type="button"
							variant="outline"
							onClick={handlePrevious}
						>
							Previous
						</ui.Button>

						<ui.Button type="submit">Next</ui.Button>
					</div>
				</form>
			</ui.Form>
		</>
	);
};

export const Route = createFileRoute(
	"/_app/new/diary/$diaryId/events/$eventId/surgery/$surgeryId/details",
)({
	component: SurgeryDetailsPage,
	loader: async (
		{ context, params }: {
			context: any;
			params: { diaryId: string; eventId: string; surgeryId: string };
		},
	) => {
		const { diaryId, eventId, surgeryId } = params;

		const diary = await context.queryClient.ensureQueryData(
			diaryByIdQueryOptions(diaryId),
		);
		if (!diary) {
			return { event: undefined, organisationId: undefined };
		}
		const surgery = diary
			? extractSurgeryById(diary, eventId, surgeryId)
			: undefined;
		return { surgery, organisationId: diary.organisation_id };
	},
});
